import { Feature, useFeatures, useUpdateFeature } from '@gripp/shared-logic';
import { CustomAlert, DataGrid, SlideLayout, Toggle } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text } from 'react-native';
import { ModalButton } from '../../shared/components/modalButton';
import { FeatureAssignmentForm } from './featureAssignmentForm';

export const Features = () => {
  const leftColumn = {
    title: 'features.title',
    mainComponent: <FeaturesBase />,
  };

  return <SlideLayout leftColumn={leftColumn} />;
};

const FeaturesBase = () => {
  const [dataRows, setDataRows] = useState(undefined);
  const [pendingToggle, setPendingToggle] = useState<{
    feature: Feature;
    enabled: boolean;
  } | null>(null);

  const { data, isLoading } = useFeatures();
  const { mutate: updateFeature } = useUpdateFeature();

  const queryClient = useQueryClient();

  const confirmToggle = useCallback(() => {
    if (!pendingToggle) return;
    updateFeature({
      input: { id: pendingToggle.feature.id, enabled: pendingToggle.enabled },
    });
    setPendingToggle(null);
  }, [pendingToggle, updateFeature]);

  const setFeatureEnabled = (enabled: boolean, feature: Feature) => {
    setPendingToggle({ feature, enabled });
  };

  const invalidateFeatureQueries = () => {
    // Invalidate specific feature queries
    queryClient.invalidateQueries({ queryKey: ['feature'] });
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: () => 'Name',
    }),
    columnHelper.accessor('status', {
      cell: (info) => {
        const feature = info.row.original as Feature;
        const currentFeature = data.items.filter(
          (f: Feature) => f.id === feature.id
        )[0];
        const isEnabled = currentFeature?.enabled ?? false;

        return (
          <Toggle
            value={isEnabled}
            onValueChange={(value) => setFeatureEnabled(value, feature)}
            size="small"
          />
        );
      },
      header: () => 'Enabled',
    }),
    columnHelper.accessor('workspaces', {
      cell: (info) => {
        const feature = info.row.original as Feature;
        const count = feature.workspaces?.length || 0;
        return (
          <ModalButton
            label=""
            customButton={<Text style={styles.linkText}>{count}</Text>}
            modalComponent={({ onComplete }) => (
              <FeatureAssignmentForm
                featureId={feature.id}
                title={`Assign ${feature.name}`}
                onComplete={() => {
                  onComplete();
                  invalidateFeatureQueries();
                }}
                initialTab="workspace"
              />
            )}
            containerStyle={styles.modalContainer}
          />
        );
      },
      header: () => 'Workspaces',
    }),
    columnHelper.accessor('accounts', {
      cell: (info) => {
        const feature = info.row.original as Feature;
        const count = feature.accounts?.length || 0;
        return (
          <ModalButton
            label=""
            customButton={<Text style={styles.linkText}>{count}</Text>}
            modalComponent={({ onComplete }) => (
              <FeatureAssignmentForm
                featureId={feature.id}
                title={`Assign ${feature.name}`}
                onComplete={() => {
                  onComplete();
                  invalidateFeatureQueries();
                }}
                initialTab="account"
              />
            )}
            containerStyle={styles.modalContainer}
          />
        );
      },
      header: () => 'Accounts',
    }),
    columnHelper.accessor('description', {
      cell: (info) => info.getValue(),
      header: () => 'Description',
    }),
  ];

  useEffect(() => {
    if (data) {
      setDataRows(data.items);
    }
  }, [data]);

  return dataRows && !isLoading ? (
    <>
      <CustomAlert
        visible={!!pendingToggle}
        hideAlert={() => setPendingToggle(null)}
        title={
          <Text>
            Are you sure you want to{' '}
            <Text style={{ fontWeight: 'bold' }}>
              {pendingToggle?.enabled ? 'enable' : 'disable'}
            </Text>{' '}
            {pendingToggle?.feature.name} globally?
          </Text>
        }
        primaryButtonLabel="Yes"
        secondaryButtonLabel="Cancel"
        action={confirmToggle}
      />
      <DataGrid
        data={dataRows}
        columns={columns as any}
        isLoading={isLoading}
      />
    </>
  ) : (
    <h1>Loading...</h1>
  );
};

const styles = StyleSheet.create({
  linkText: {
    fontSize: 16,
    cursor: 'pointer' as any,
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: 10,
  },
});
